import React from 'react';

// A read-only value shown as a form field: a label over a grey box, the same
// size as the agreement's References fields. tone: 'strong' (blue, a key
// figure) or 'alert' (red).
// The label and the box, shared with the inputs of the edit forms, so a
// field looks the same whether it is shown or edited.
export const FIELD_LABEL = 'block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1 truncate';
// The same size and look as an input (index.css styles every input, select
// and textarea this way), so a value shown here lines up with one edited.
export const FIELD_BOX = 'w-full px-2.5 py-1.5 text-xs leading-4 min-h-[30px] border rounded bg-white dark:bg-slate-800 break-words';
export const FIELD_BORDER = 'border-gray-300 dark:border-slate-500';
// An input: the global field look (index.css), full width, with a red edge
// while a required one is empty.
export const fieldInputClass = (missing = false) => `w-full ${missing ? '!border-rose-300 dark:!border-rose-700/60' : ''}`.trim();
// A read-only input or a locked select that shows a value: the same as a
// DisplayField (white, plain text), not the faded disabled look.
export const FIELD_READONLY = 'cursor-default disabled:!bg-white dark:disabled:!bg-slate-800 disabled:!text-gray-700 dark:disabled:!text-slate-200 disabled:!cursor-default read-only:!bg-white dark:read-only:!bg-slate-800';

const DisplayField = ({ label, children, tone = null, className = '' }) => {
  const empty = children === null || children === undefined || children === '' || children === '-';
  // tone: 'strong' (blue, a key figure), 'alert' (red, bad), 'good' (green);
  // the colour names 'blue' / 'red' / 'green' mean the same.
  const t = { red: 'alert', green: 'good', blue: 'strong' }[tone] || tone;
  const text = t === 'alert'
    ? 'text-red-600 dark:text-red-400 font-semibold'
    : t === 'good'
      ? 'text-emerald-700 dark:text-emerald-400 font-semibold'
      : t === 'strong'
        ? 'text-blue-700 dark:text-blue-300 font-semibold'
        : 'text-gray-700 dark:text-slate-200';
  return (
    <div className={`min-w-0 ${className}`}>
      <div className={FIELD_LABEL} title={typeof label === 'string' ? label : undefined}>{label}</div>
      <div className={`${FIELD_BOX} ${FIELD_BORDER} ${empty ? 'text-gray-400 dark:text-slate-500' : text}`}>
        {empty ? '—' : children}
      </div>
    </div>
  );
};

// Each group its own colour, so the groups are easy to tell apart. The
// classes are written out in full so the styles are kept in the build.
const TONES = {
  blue: { box: 'bg-blue-50/70 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800/50', head: 'text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800/50' },
  emerald: { box: 'bg-emerald-50/70 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800/50', head: 'text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/50' },
  amber: { box: 'bg-amber-50/70 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800/50', head: 'text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800/50' },
  violet: { box: 'bg-violet-50/70 border-violet-200 dark:bg-violet-900/10 dark:border-violet-800/50', head: 'text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800/50' },
  teal: { box: 'bg-teal-50/70 border-teal-200 dark:bg-teal-900/10 dark:border-teal-800/50', head: 'text-teal-800 dark:text-teal-200 border-teal-200 dark:border-teal-800/50' },
  slate: { box: 'bg-slate-50 border-slate-200 dark:bg-slate-800/60 dark:border-slate-700', head: 'text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700' },
};

// A group of fields under its own heading, in its own colour. wide: the whole
// row; otherwise half of it on wide screens, so two groups sit side by side.
// bare: the children as they are (a table), not laid out as fields.
export const FieldGroup = ({ title, children, tone = 'slate', wide = false, bare = false, className = '' }) => {
  const t = TONES[tone] || TONES.slate;
  return (
    <div className={`${wide ? 'col-span-full' : 'col-span-full xl:col-span-3'} rounded-lg border p-3 ${t.box} ${className}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide pb-1 mb-2 border-b ${t.head}`}>
        {title}
      </div>
      {bare ? children : (
        <div className={`grid gap-3 ${wide ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {children}
        </div>
      )}
    </div>
  );
};

export default DisplayField;
